import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LogOut,
  User,
  Plus,
  ShieldAlert,
  PlusCircle,
  PanelLeft,
  PanelRight,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type HeaderProps = {
  title: string;
  actionButton?: ReactNode;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
};

const Header = ({
  title,
  actionButton,
  sidebarCollapsed = false,
  onToggleSidebar,
}: HeaderProps) => {
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionAction, setPermissionAction] = useState("");

  // Check if current user has admin privileges
  const hasAdminPrivileges = (): boolean => {
    if (!user || !user.roles) return false;

    // Check if the user has any system admin role
    return user.roles.some((role) => role.isSystem === true);
  };

  // Handle action button click with permission check
  const handleActionClick = (destination: string, actionName: string) => {
    if (
      (actionName === "Add User" || actionName === "Add Section") &&
      !hasAdminPrivileges()
    ) {
      setPermissionAction(
        actionName === "Add User" ? "add users" : "add sections"
      );
      setPermissionDialogOpen(true);
      return;
    }

    navigate(destination);
  };

  // Determine which Add button to show based on title
  const showAddButtonType =
    title === "Books"
      ? "book"
      : title === "Collections"
      ? "collection"
      : title === "Users"
      ? "user"
      : title === "Sections"
      ? "section"
      : title === "Subscription Plans"
      ? "subscription-plan"
      : title === "Promotions"
      ? "promotion"
      : title === "Reading Rewards"
      ? "reading-reward"
      : title === "Payment Methods"
      ? "payment-method"
      : null;

  // Create a default action button based on the page type
  const defaultActionButton = showAddButtonType ? (
    <Button
      onClick={() =>
        handleActionClick(
          showAddButtonType === "book"
            ? "/admin/books/new"
            : showAddButtonType === "collection"
            ? "/admin/collections/new"
            : showAddButtonType === "section"
            ? "/admin/sections/new"
            : showAddButtonType === "subscription-plan"
            ? "/admin/subscription-plans/new"
            : showAddButtonType === "promotion"
            ? "/admin/promotions/new"
            : showAddButtonType === "reading-reward"
            ? "/admin/reading-rewards/new"
            : showAddButtonType === "payment-method"
            ? "/admin/system/payment-methods/new"
            : "/admin/users/new",
          showAddButtonType === "book"
            ? "Add Book"
            : showAddButtonType === "collection"
            ? "Add Collection"
            : showAddButtonType === "section"
            ? "Add Section"
            : showAddButtonType === "subscription-plan"
            ? "Add Subscription Plan"
            : showAddButtonType === "promotion"
            ? "Add Promotion"
            : showAddButtonType === "reading-reward"
            ? "Add Milestone"
            : showAddButtonType === "payment-method"
            ? "Add Payment Method"
            : "Add User"
        )
      }
      className="flex items-center gap-2"
    >
      {showAddButtonType === "promotion" ? (
        <Plus className="h-4 w-4" />
      ) : (
        <Plus className="h-4 w-4" />
      )}
      <span className="hidden sm:inline">
        {showAddButtonType === "book"
          ? "Add Book"
          : showAddButtonType === "collection"
          ? "Add Collection"
          : showAddButtonType === "section"
          ? "Add Section"
          : showAddButtonType === "subscription-plan"
          ? "Add Subscription Plan"
          : showAddButtonType === "promotion"
          ? "Add Promotion"
          : showAddButtonType === "reading-reward"
          ? "Add Milestone"
          : showAddButtonType === "payment-method"
          ? "Add Payment Method"
          : "Add User"}
      </span>
      <span className="sm:hidden">
        {showAddButtonType === "promotion" ? "Add" : "Add"}
      </span>
    </Button>
  ) : null;

  // Use the provided action button or the default one
  const buttonToShow = actionButton || defaultActionButton;

  return (
    <header className="sticky top-0 z-10 h-16 border-b bg-background">
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Desktop sidebar toggle button */}
          {!isMobile && onToggleSidebar && (
            <Button
              variant="outline"
              size="icon"
              onClick={onToggleSidebar}
              className="shrink-0 text-sidebar-primary bg-sidebar-accent/10 hover:bg-sidebar-accent hover:text-sidebar-foreground border-sidebar-border transition-colors"
            >
              {sidebarCollapsed ? (
                <PanelRight className="h-5 w-5" />
              ) : (
                <PanelLeft className="h-5 w-5" />
              )}
            </Button>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="shrink-0 lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0">
                <Sidebar collapsed={false} />
              </SheetContent>
            </Sheet>
          )}

          <h1 className="text-xl font-semibold truncate">{title}</h1>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {buttonToShow}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 hover:bg-accent"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.avatarUrl} alt={user.name} />
                    <AvatarFallback>
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="p-2 text-sm font-medium">{user.email}</div>
                <DropdownMenuSeparator />
                {user.roles && user.roles.length > 0 && (
                  <div className="p-2 text-xs text-muted-foreground">
                    <span className="font-semibold">Roles:</span>{" "}
                    {user.roles.map((role) => role.name).join(", ")}
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => navigate("/admin/profile")}
                >
                  <User className="mr-2 h-4 w-4" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => signOut()}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={setPermissionDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Permission Required
            </DialogTitle>
            <DialogDescription>
              You need administrator privileges to {permissionAction}. This
              action is restricted to system administrators only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPermissionDialogOpen(false)}>
              Understood
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
